import { Grid, Typography } from '@material-ui/core';
import tinycolor from 'tinycolor2';

import styles from './spinner.module.scss';

interface SpinnerProps {
  active?: boolean;
  /**
   * The background color for the Spinner
   */
  background?: string;
  message?: string;
}

const lightenRate = 10;

function lighten(hex: string): string {
  return tinycolor(hex).lighten(lightenRate).toHexString();
}

// Default SF color
let primaryColor = '#69936C';
switch (process.env.REACT_APP_APPLICATION_NAME) {
  case 'analyze':
    primaryColor = '#6296bc';
    break;
  case 'forecast':
    primaryColor = '#8f64a3';
    break;
  case 'fulfill':
    primaryColor = '#ba3636';
    break;
  case 'produce':
    primaryColor = '#db6e14';
    break;
  case 'report':
    primaryColor = '#e8b023';
    break;
  case 'restock':
    primaryColor = '#63B76B';
    break;
  default:
    break;
}

const lightColor = lighten(lighten(primaryColor));

export default function Spinner({ active = false, message = '', background = '#ffffffaa' }: SpinnerProps) {
  const colors = getColorsArray(lightColor, primaryColor, 5);

  return (
    <Grid
      className={`${styles.spinner} ${active ? '' : styles.hidden}`}
      style={{ backgroundColor: background }}
      direction="column"
      container
      justifyContent="center"
      alignItems="center"
    >
      <svg fill="#fff" height="30" viewBox="0 0 210 30" width="210" xmlns="http://www.w3.org/2000/svg">
        <circle className={styles.dot} style={{ fill: colors[0] }} cx="15" cy="15" r="15"></circle>
        <circle className={styles.dot} style={{ fill: colors[1] }} cx="60" cy="15" r="15"></circle>
        <circle className={styles.dot} style={{ fill: colors[2] }} cx="105" cy="15" r="15"></circle>
        <circle className={styles.dot} style={{ fill: colors[3] }} cx="150" cy="15" r="15"></circle>
        <circle className={styles.dot} style={{ fill: colors[4] }} cx="195" cy="15" r="15"></circle>
      </svg>
      {message && (
        <Typography variant="h3" className={styles.message}>
          {message}
        </Typography>
      )}
    </Grid>
  );
}

/**
 * A linear interpolator for hex colors.
 *
 * Based on:
 * https://gist.github.com/rosszurowski/67f04465c424a9bc0dae
 *
 * @param {Number} a  (hex color start val)
 * @param {Number} b  (hex color end val)
 * @param {Number} amount  (the amount to fade from a to b)
 *
 * @example
 * // returns 0x7f7f7f
 * interpolateColor(0x000000, 0xffffff, 0.5)
 *
 * @returns {Number}
 */
function interpolateColor(a: number, b: number, amount: number): string {
  const ar = a >> 16,
    ag = (a >> 8) & 0xff,
    ab = a & 0xff,
    br = b >> 16,
    bg = (b >> 8) & 0xff,
    bb = b & 0xff,
    rr = Math.round(ar + amount * (br - ar)),
    rg = Math.round(ag + amount * (bg - ag)),
    rb = Math.round(ab + amount * (bb - ab));

  const red = (rr << 16).toString(16).substring(0, 2).padStart(2, '0');
  const green = (rg << 8).toString(16).substring(0, 2).padStart(2, '0');
  const blue = (rb | 0).toString(16).substring(0, 2).padStart(2, '0');

  return `#${red}${green}${blue}`;
}

/**
 * Given start and end colors for a gradient, returns an array of n interpolated colors as hexadecimal strings
 */
function getColorsArray(start: string, end: string, numberOfColors: number): string[] {
  const colorInterval: number = 1 / numberOfColors;

  const startHex = `0x${start.substring(1)}`;
  const endHex = `0x${end.substring(1)}`;

  // const num = Number(startHex);
  const startNumber = parseInt(startHex, 16);
  const endNumber = parseInt(endHex, 16);

  let colors: string[] = [];
  for (let i = 0; i < numberOfColors; i++) {
    const colorDecimal = i * colorInterval;
    const color = interpolateColor(startNumber, endNumber, colorDecimal);
    colors.push(color);
  }

  return colors;
}
